import type { DictFragment } from '../translations';

/** App shell: topbar, sidebar navigation, page titles, profile menu, login, nav guard. */
export const shell: DictFragment = {
  fr: {
    // page titles (topbar breadcrumb)
    // live: les autres personnes sur le même écran en ce moment
    'live.here': 'Aussi ici : {names}',
    'live.moving': '{name} déplace cette carte',
    'live.viewing': '{name} a ouvert cette carte',
    'live.moved': '{name} vient de déplacer cette carte',
    'live.changedBy': '{name} vient de modifier ce projet.',
    'live.changedBySomeone': 'Quelqu’un vient de modifier ce projet.',
    'live.reload': 'Recharger',
    'page.portfolio': 'Portefeuille',
    'page.pipeline': 'Pipeline',
    'page.inbox': 'Boîte de réception',
    'page.projects': 'Projets',
    'page.people': 'Personnes',
    'page.infrastructure': 'Infrastructure',
    'page.deliverables': 'Livrables',
    'page.admin': 'Admin',
    'page.submit': 'Soumettre un projet',
    'page.profile': 'Profil',
    'page.tasks': 'Organisation tâches',
    'page.tasksCalendar': 'Mon calendrier',
    'page.tasksKanban': 'Kanban Fonction',
    'page.tasksPlanning': 'Planification des tâches',
    'page.project': 'Détail du projet',
    'page.council': 'Conseil de Pilotage',
    'page.gerance': 'Gérance',

    // topbar
    'shell.searchProjects': 'Rechercher des projets…',
    'shell.clearSearch': 'Effacer la recherche',
    'shell.myProfile': 'Mon profil',
    'shell.adminBadge': 'Admin',
    'shell.directionBadge': 'Direction',

    // sidebar
    'shell.collapse': '‹ Réduire',
    'shell.nav.portfolio': 'Portefeuille',
    'shell.nav.pipeline': 'Pipeline',
    'shell.nav.inbox': 'Boîte de réception',
    'shell.nav.submit': 'Soumettre un projet',
    'shell.nav.myChanges': 'Mes modifications',
    'shell.nav.pmo': 'PMO',
    'shell.nav.council': 'Conseil',
    'shell.nav.gerance': 'Gérance',
    'shell.nav.tasks': 'Tâches',
    'shell.nav.overview': 'Vue d’ensemble',
    'shell.inboxNew': '{n} nouvelle(s) initiative(s) depuis votre dernière visite',
    'shell.nav.recentProjects': 'Projets récents',
    'shell.nav.tasksProjects': 'Organisation tâches',
    'shell.nav.tasksCalendar': 'Mon calendrier',
    'shell.nav.tasksPlanning': 'Planification des tâches',
    'shell.nav.tasksKanban': 'Kanban Fonction',
    'shell.nav.inventories': 'Inventaires',
    'shell.nav.projects': 'Projets',
    'shell.nav.people': 'Personnes',
    'shell.nav.infrastructure': 'Infrastructure',
    'shell.nav.deliverables': 'Livrables',

    // impersonation
    'shell.impersonating': 'Consultation en tant que {name} — imité par {by}',
    'shell.stopImpersonating': 'Arrêter l’imitation',

    // e-mails waiting because of my changes (topbar)
    'pendingEmails.title': '{n} e-mail(s) en attente suite à vos modifications',
    'pendingEmails.heading': 'E-mails en attente',
    'pendingEmails.help': 'Vos modifications partent par e-mail, regroupées par personne, une fois qu’il n’y a plus de nouveau changement pour elle pendant un moment. Vous pouvez aussi les envoyer maintenant.',
    'pendingEmails.changes': '{n} changement(s)',
    'pendingEmails.inMinutes': 'envoi dans {n} min',
    'pendingEmails.sendNow': 'Envoyer maintenant',
    'pendingEmails.sent': '{n} e-mail(s) envoyé(s).',
    'pendingEmails.addNote': 'Ajouter un message',
    'pendingEmails.noteTitle': 'Titre du message',
    'pendingEmails.noteText': 'Votre message…',
    'pendingEmails.editNote': 'Modifier le message',
    'pendingEmails.noProject': 'Sans projet',
    'pendingEmails.comment': 'Ajouter un commentaire',
    'pendingEmails.editComment': 'Modifier mon commentaire',
    'pendingEmails.commentPlaceholder': 'Commentaire affiché sous cet élément dans l’e-mail…',
    'pendingEmails.removeComment': 'Retirer le commentaire',
    'pendingEmails.remove': 'Retirer de l’e-mail',
    'pendingEmails.confirmRemove': 'Retirer « {title} » de cet e-mail ?',

    // dev environment banner (DEPLOY_ENV=dev)
    'shell.devBanner.label': 'Environnement DEV',
    'shell.devBanner.text': 'Environnement de test — les modifications faites ici ne sont pas reprises dans le PMO et peuvent être écrasées à tout moment. Pour le travail réel, utilisez :',

    // notification bell (notification-kit labels)
    'notifBell.bellTitle': 'Notifications',
    'notifBell.dropdownTitle': 'Notifications',
    'notifBell.markAllRead': 'Tout marquer comme lu',
    'notifBell.empty': 'Aucune notification',
    'notifBell.justNow': 'à l’instant',
    'notifBell.minutesAgo': 'il y a {m} min',
    'notifBell.hoursAgo': 'il y a {h} h',
    'notifBell.daysAgo': 'il y a {d} j',

    // profile menu
    'profile.menuAria': 'Ouvrir le menu du profil',
    'profile.title': 'Profil',

    // login page (app wrapper)
    'login.subtitle': 'Outil de gestion de projets',
    'login.oidcButton': 'Se connecter avec Authentik',

    // login form (auth-sso kit labels)
    'login.emailLabel': 'E-mail',
    'login.passwordLabel': 'Mot de passe',
    'login.passwordPlaceholder': 'Saisissez votre mot de passe',
    'login.submit': 'Connexion',
    'login.submitting': 'Connexion…',
    'login.footerOpen': 'Inscription ouverte — le premier utilisateur devient administrateur',
    'login.footerAllowlist': 'Seules les adresses e-mail pré-approuvées peuvent se connecter',
    'login.errEmailRequired': 'L’e-mail est requis',
    'login.errPasswordRequired': 'Le mot de passe est requis',
    'login.errLoginFailed': 'Échec de la connexion',
    'login.errNotAuthorized': 'Votre e-mail n’est pas autorisé à accéder à cette application. Contactez un administrateur.',
    'login.errOidcFailed': 'Échec de l’authentification. Veuillez réessayer.',
    'login.errNoEmail': 'Aucune adresse e-mail n’a été reçue du fournisseur d’identité.',

    // navigation guard (unsaved changes)
    'guard.title': 'Quitter sans enregistrer ?',
    'guard.message': 'Vous avez des modifications non enregistrées. Si vous quittez maintenant, elles seront perdues.',
    'guard.leave': 'Quitter',
  },
  en: {
    // live: other people on the same screen right now
    'live.here': 'Also here: {names}',
    'live.moving': '{name} is moving this card',
    'live.viewing': '{name} has this card open',
    'live.moved': '{name} just moved this card',
    'live.changedBy': '{name} just changed this project.',
    'live.changedBySomeone': 'Someone just changed this project.',
    'live.reload': 'Reload',
    'page.portfolio': 'Portfolio',
    'page.pipeline': 'Pipeline',
    'page.inbox': 'Inbox',
    'page.projects': 'Projects',
    'page.people': 'People',
    'page.infrastructure': 'Infrastructure',
    'page.deliverables': 'Deliverables',
    'page.admin': 'Admin',
    'page.submit': 'Submit Project',
    'page.profile': 'Profile',
    'page.tasks': 'Task Organization',
    'page.tasksCalendar': 'My calendar',
    'page.tasksKanban': 'Function Kanban',
    'page.tasksPlanning': 'Task Planning',
    'page.project': 'Project Detail',
    'page.council': 'Steering Committee',
    'page.gerance': 'Management',

    'shell.searchProjects': 'Search projects…',
    'shell.clearSearch': 'Clear search',
    'shell.myProfile': 'My Profile',
    'shell.adminBadge': 'Admin',
    'shell.directionBadge': 'Direction',

    'shell.collapse': '‹ Collapse',
    'shell.nav.portfolio': 'Portfolio',
    'shell.nav.pipeline': 'Pipeline',
    'shell.nav.inbox': 'Inbox',
    'shell.nav.submit': 'Submit Project',
    'shell.nav.myChanges': 'My Changes',
    'shell.nav.pmo': 'PMO',
    'shell.nav.council': 'Council',
    'shell.nav.gerance': 'Management',
    'shell.nav.tasks': 'Tasks',
    'shell.nav.overview': 'Overview',
    'shell.inboxNew': '{n} new initiative(s) since your last visit',
    'shell.nav.recentProjects': 'Recent projects',
    'shell.nav.tasksProjects': 'Task Organization',
    'shell.nav.tasksCalendar': 'My calendar',
    'shell.nav.tasksPlanning': 'Task Planning',
    'shell.nav.tasksKanban': 'Function Kanban',
    'shell.nav.inventories': 'Inventories',
    'shell.nav.projects': 'Projects',
    'shell.nav.people': 'People',
    'shell.nav.infrastructure': 'Infrastructure',
    'shell.nav.deliverables': 'Deliverables',

    'shell.impersonating': 'Viewing as {name} — impersonated by {by}',
    'shell.stopImpersonating': 'Stop Impersonating',

    'pendingEmails.title': '{n} e-mail(s) waiting because of your changes',
    'pendingEmails.heading': 'E-mails waiting',
    'pendingEmails.help': 'Your changes are e-mailed, bundled per person, once nothing new has come in for them for a while. You can also send them now.',
    'pendingEmails.changes': '{n} change(s)',
    'pendingEmails.inMinutes': 'sends in {n} min',
    'pendingEmails.sendNow': 'Send now',
    'pendingEmails.sent': '{n} e-mail(s) sent.',
    'pendingEmails.addNote': 'Add a message',
    'pendingEmails.noteTitle': 'Message title',
    'pendingEmails.noteText': 'Your message…',
    'pendingEmails.editNote': 'Edit the message',
    'pendingEmails.noProject': 'No project',
    'pendingEmails.comment': 'Add a comment',
    'pendingEmails.editComment': 'Edit my comment',
    'pendingEmails.commentPlaceholder': 'Comment shown under this item in the e-mail…',
    'pendingEmails.removeComment': 'Remove the comment',
    'pendingEmails.remove': 'Remove from the e-mail',
    'pendingEmails.confirmRemove': 'Remove “{title}” from this e-mail?',

    'shell.devBanner.label': 'DEV environment',
    'shell.devBanner.text': 'Test environment — changes made here do not carry over to the PMO and can be overwritten at any time. For real work, use:',

    // notification bell (notification-kit labels)
    'notifBell.bellTitle': 'Notifications',
    'notifBell.dropdownTitle': 'Notifications',
    'notifBell.markAllRead': 'Mark all read',
    'notifBell.empty': 'No notifications',
    'notifBell.justNow': 'just now',
    'notifBell.minutesAgo': '{m}m ago',
    'notifBell.hoursAgo': '{h}h ago',
    'notifBell.daysAgo': '{d}d ago',

    'profile.menuAria': 'Open profile menu',
    'profile.title': 'Profile',

    'login.subtitle': 'Project Planning Tool',
    'login.oidcButton': 'Log in with Authentik',

    'login.emailLabel': 'Email',
    'login.passwordLabel': 'Password',
    'login.passwordPlaceholder': 'Enter your password',
    'login.submit': 'Login',
    'login.submitting': 'Logging in…',
    'login.footerOpen': 'Open registration — first user becomes admin',
    'login.footerAllowlist': 'Only pre-approved email addresses can log in',
    'login.errEmailRequired': 'Email is required',
    'login.errPasswordRequired': 'Password is required',
    'login.errLoginFailed': 'Login failed',
    'login.errNotAuthorized': 'Your email is not authorized to access this application. Contact an administrator.',
    'login.errOidcFailed': 'Authentication failed. Please try again.',
    'login.errNoEmail': 'No email was received from the identity provider.',

    'guard.title': 'Leave without saving?',
    'guard.message': "You have unsaved changes. If you leave now, they'll be lost.",
    'guard.leave': 'Leave',
  },
};
